import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   🍎 APPLE CLEAN EDGE BUTTON
   - Clean, sharp edges
   - Minimal shadows
   - Simple, elegant design
------------------------------------------------------------------- */

const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-2",
    "whitespace-nowrap font-medium select-none",
    "transition-all duration-200 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",

    /* 🍏 Clean shape with subtle rounding */
    "rounded-lg overflow-hidden",

    /* 🍏 Minimal shadow */
    "shadow-sm",

    /* 🍏 Subtle edge highlight - clean edges only */
    "before:absolute before:inset-0 before:pointer-events-none",
    "before:rounded-[inherit]",
    "before:shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]",
  ].join(" "),
  {
      variants: {
        variant: {
          default:
            "bg-primary text-primary-foreground",

          outline:
            "bg-white dark:bg-black text-foreground",

          secondary:
            "bg-secondary text-secondary-foreground",

          ghost:
            "bg-transparent shadow-none",

          destructive:
            "bg-destructive text-destructive-foreground",

          link:
            "bg-transparent shadow-none text-primary underline-offset-4 underline before:hidden",
        },

      size: {
        sm: "h-9 px-4 text-sm",
        default: "h-11 px-6 text-sm",
        lg: "h-12 px-8 text-base",
        icon: "h-11 w-11 p-0 rounded-lg",
      },
    },

    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
